import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { plannerTimelineInstantMs } from '@/actions/Helper';
import {
  getTimelineCirclePaintFromApiColor,
  TIMELINE_COLOR_GREEN_SOLID,
} from '@/utils/availHistoryTimelinePaint';
import styles from './PlannerAvailabilityTimeline.module.css';

const HOUR_MS = 60 * 60 * 1000;
const HALF_HOUR_MS = 30 * 60 * 1000;
/**
 * Single knob for horizontal time scale in this component.
 * minute width (px) = hourWidthPx / 60
 */
const PLANNER_TIMELINE_SCALE = {
  hourWidthPx: 140,
};

function getPlannerTimelinePxPerMs() {
  return PLANNER_TIMELINE_SCALE.hourWidthPx / HOUR_MS;
}

function plannerTimelineDurationMsToWidthPx(durationMs) {
  return durationMs * getPlannerTimelinePxPerMs();
}

const TIMELINE_EDGE_PADDING_PX = 28;
const AXIS_Y_PX = 140;
const TRACK_HEIGHT_PX = 2;
const MILESTONE_LANE_GAP_PX = 46;
/** Vertical offset per stacked appointment lane (lane 0 sits on axis). */
const APPOINTMENT_LANE_GAP_PX = 38;
const APPOINTMENT_BAR_HEIGHT_PX = 20;
const APPOINTMENT_BUFFER_DEFAULT_MS = 30 * 60 * 1000;
const NODE_LABEL_BASE_OFFSET_PX = 10;
const NODE_LABEL_ESTIMATED_HEIGHT_PX = 56;
/** Minimum horizontal overlap with appointment safe-zone (light padded range). */
const APPOINTMENT_LABEL_OVERLAP_MIN_PX = 8;
/** Must match `.nodeLabel { width/max-width }` in the CSS module */
const NODE_LABEL_LAYOUT_WIDTH_PX = 120;
const NODE_LABEL_ABOVE_GAP_PX = 8;
/** Move “labels above axis” this much closer to the axis (down). */
const NODE_LABEL_ABOVE_TOWARD_AXIS_PX = 25;
/** Pull 2nd+ stacked row above axis closer to the main axis (and to row 1). */
const ABOVE_AXIS_SECOND_ROW_PULL_DOWN_PX = 18;
/** When a 2nd milestone row exists above axis, drop Autobook Window labels slightly. */
const WINDOW_LABEL_EXTRA_DOWN_WHEN_SECOND_ABOVE_ROW_PX = 18;
/**
 * When only ONE row of milestones sits above the axis, blend window Y toward compact
 * so labels do not sit on an extra-high “third” band with empty space below.
 */
const WINDOW_LABEL_SINGLE_ABOVE_ROW_BLEND = 0.78;
/**
 * Stack milestone labels to the next row only if horizontal overlap is at least this many px.
 * Prevents “1–2px kiss overlap” (common when hourWidth is small) from pushing nodes down a lane.
 */
const MILESTONE_LABEL_MIN_OVERLAP_TO_STACK_PX = 20;
const WINDOW_LABEL_TOP_PX = 8;
/** Extra vertical offset for Autobook Window labels/lines (smaller = higher row). */
const AUTOBOOK_WINDOW_VERTICAL_NUDGE_PX = -20;
/** In compact mode (no top 2nd row), keep windows closer to the main axis. */
const WINDOW_LABEL_COMPACT_GAP_FROM_AXIS_PX = 85;

const COLOR_BLUE = '#2563EB';
const COLOR_GREEN = '#10B981';
const COLOR_GRAY = '#9CA3AF';
const COLOR_RED = '#EF4444';

function normalizeTitleForStyle(title) {
  return String(title || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function circlePaint(apiColor) {
  const mapped = getTimelineCirclePaintFromApiColor(apiColor);
  if (mapped) return mapped;
  if (apiColor != null && String(apiColor).trim() !== '') {
    return {
      background: String(apiColor).trim(),
      border: '2px solid transparent',
      sizePx: 12,
    };
  }
  return {
    background: TIMELINE_COLOR_GREEN_SOLID,
    border: '2px solid transparent',
    sizePx: 12,
  };
}

function resolveNodeCircleStyle(node) {
  const title = normalizeTitleForStyle(node?.title);
  if (title === 'delivery' || title === 'unload done') {
    return {
      sizePx: 12,
      background: '#fff',
      border: `2px solid ${COLOR_BLUE}`,
    };
  }
  if (title === 'target return') {
    return {
      sizePx: 14,
      background: '#fff',
      border: `3px solid ${COLOR_BLUE}`,
    };
  }
  if (title === 'autobook window start' || title === 'autobook window end') {
    return {
      sizePx: 12,
      background: '#fff',
      border: `2px dashed ${COLOR_GREEN}`,
    };
  }
  if (title === 'booked apt (manual)') {
    return { sizePx: 10, background: COLOR_BLUE, border: 'none' };
  }
  if (title === 'booked apt (autobook)') {
    return { sizePx: 10, background: COLOR_GREEN, border: 'none' };
  }
  if (title === 'booked apt (external)') {
    return { sizePx: 10, background: COLOR_GRAY, border: 'none' };
  }
  if (title === 'lfd') {
    return { sizePx: 14, background: COLOR_RED, border: 'none' };
  }
  return circlePaint(node?.apiColor);
}

function isAppointmentNode(node) {
  const title = normalizeTitleForStyle(node?.title);
  return title.startsWith('booked apt');
}

function isWindowBoundaryNode(node) {
  const title = normalizeTitleForStyle(node?.title);
  return title === 'autobook window start' || title === 'autobook window end';
}

function isWindowStartNode(node) {
  return normalizeTitleForStyle(node?.title) === 'autobook window start';
}

function isWindowEndNode(node) {
  return normalizeTitleForStyle(node?.title) === 'autobook window end';
}

function estimateLabelWidthPx(
  text,
  min = NODE_LABEL_LAYOUT_WIDTH_PX,
  max = NODE_LABEL_LAYOUT_WIDTH_PX
) {
  const len = String(text || '')
    .replace(/\s+/g, ' ')
    .trim().length;
  const px = len * 6.8 + 28;
  return Math.max(min, Math.min(max, px));
}

function placeInLanes(items, getBoundsPx) {
  const lanesRightEdge = [];
  return items.map((item) => {
    const { left, right } = getBoundsPx(item);
    let laneIndex = 0;
    while (
      lanesRightEdge[laneIndex] !== undefined &&
      left <= lanesRightEdge[laneIndex]
    ) {
      laneIndex += 1;
    }
    lanesRightEdge[laneIndex] = right + 8;
    return { ...item, laneIndex };
  });
}

/**
 * Stack labels vertically only when a label would collide with an **earlier** label on the
 * same lane (left-to-right sweep). Avoids pushing node N into lane 2 just because lane 1 is
 * still “busy” farther to the right — that produced “Unload Done always lowest” false positives.
 */
function intervalOverlapWidth(leftA, rightA, leftB, rightB) {
  const left = Math.max(leftA, leftB);
  const right = Math.min(rightA, rightB);
  return Math.max(0, right - left);
}

function placeSequentialNonOverlappingLanes(
  sortedItems,
  getBoundsPx,
  minOverlapPx = MILESTONE_LABEL_MIN_OVERLAP_TO_STACK_PX
) {
  /** { left, right } of the last label placed in each lane */
  const laneLast = [];

  return sortedItems.map((item) => {
    const b = getBoundsPx(item);
    let laneIndex = 0;
    while (laneLast[laneIndex] != null) {
      const last = laneLast[laneIndex];
      const ow = intervalOverlapWidth(last.left, last.right, b.left, b.right);
      if (ow < minOverlapPx) break;
      laneIndex += 1;
    }
    laneLast[laneIndex] = b;
    return { ...item, laneIndex };
  });
}

function buildHourTicks(axisStartMs, axisEndMs) {
  const ticks = [];
  const firstTick = ceilHourMs(axisStartMs);
  const lastTick = floorHourMs(axisEndMs);
  for (let t = firstTick; t <= lastTick; t += HOUR_MS) {
    ticks.push({ ms: t });
  }
  return ticks;
}

function hourLabel(ms) {
  const d = new Date(ms);
  const hh = String(d.getHours()).padStart(2, '0');
  return `${hh}:00`;
}

function dateLabel(ms) {
  const d = new Date(ms);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}-${dd}`;
}

/** Date line under hour ticks, e.g. "Apr 11" */
function tickDateLabel(ms) {
  const d = new Date(ms);
  return d
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    .replace(',', '');
}

function floorHourMs(ms) {
  return Math.floor(ms / HOUR_MS) * HOUR_MS;
}

function ceilHourMs(ms) {
  return Math.ceil(ms / HOUR_MS) * HOUR_MS;
}

function ceilHalfHourMs(ms) {
  return Math.ceil(ms / HALF_HOUR_MS) * HALF_HOUR_MS;
}

function getAppointmentWindowMs(node, fallbackMs) {
  const startMs = Number.isFinite(node?.appointmentStartMs)
    ? node.appointmentStartMs
    : plannerTimelineInstantMs(node?.appointmentStartIso);
  const endMs = Number.isFinite(node?.appointmentEndMs)
    ? node.appointmentEndMs
    : plannerTimelineInstantMs(node?.appointmentEndIso);
  const safeStart = startMs != null ? startMs : fallbackMs;
  const safeEnd = endMs != null && endMs >= safeStart ? endMs : safeStart;
  const bufferBeforeMin = Number(node?.appointmentBufferBeforeMin);
  const bufferAfterMin = Number(node?.appointmentBufferAfterMin);
  const beforeMs =
    Number.isFinite(bufferBeforeMin) && bufferBeforeMin >= 0
      ? bufferBeforeMin * 60 * 1000
      : APPOINTMENT_BUFFER_DEFAULT_MS;
  const afterMs =
    Number.isFinite(bufferAfterMin) && bufferAfterMin >= 0
      ? bufferAfterMin * 60 * 1000
      : APPOINTMENT_BUFFER_DEFAULT_MS;
  return {
    startMs: safeStart,
    endMs: safeEnd,
    paddedStartMs: safeStart - beforeMs,
    paddedEndMs: safeEnd + afterMs,
  };
}

function durationMsToPx(durationMs) {
  return plannerTimelineDurationMsToWidthPx(durationMs);
}

function appointmentBarTopOffsetPx(laneIndex) {
  return laneIndex * APPOINTMENT_LANE_GAP_PX;
}

export const PlannerAvailabilityTimeline = forwardRef(function PlannerAvailabilityTimeline(
  { windowStartLabel, windowEndLabel, nodes, onActiveNavItemChange },
  ref
) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  const viewportRef = useRef(null);
  const [viewportWidthPx, setViewportWidthPx] = useState(0);
  const [activeNavIndex, setActiveNavIndex] = useState(0);

  useEffect(() => {
    setNowMs(Date.now());
    const id = setInterval(() => {
      setNowMs(Date.now());
    }, 60 * 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries?.[0]?.contentRect?.width;
      if (typeof w === 'number' && Number.isFinite(w)) {
        setViewportWidthPx(w);
      }
    });
    ro.observe(el);
    setViewportWidthPx(el.getBoundingClientRect().width || 0);
    return () => ro.disconnect();
  }, []);

  const timeline = useMemo(() => {
    const resolveNodeMs = (node) => {
      if (Number.isFinite(node?.timelineMs)) return node.timelineMs;
      if (
        isAppointmentNode(node) &&
        Number.isFinite(node?.appointmentStartMs)
      ) {
        return node.appointmentStartMs;
      }
      return plannerTimelineInstantMs(node?.atIso);
    };

    const enriched = [...(nodes || [])]
      .map((node, idx) => ({
        node,
        idx,
        atMs: resolveNodeMs(node),
      }))
      .filter((x) => x.atMs != null);

    const sorted = [...enriched].sort((a, b) =>
      a.atMs === b.atMs ? a.idx - b.idx : a.atMs - b.atMs
    );

    if (sorted.length === 0) return null;

    const firstAt = sorted[0].atMs;
    const lastAt = sorted[sorted.length - 1].atMs;
    const appointmentAxisEndCandidates = enriched
      .filter((x) => isAppointmentNode(x.node))
      .map((x) => {
        const range = getAppointmentWindowMs(x.node, x.atMs);
        // Keep half-hour tail after the light-green appointment buffer.
        return range.paddedEndMs + HALF_HOUR_MS;
      })
      .filter((v) => Number.isFinite(v));
    const lastVisualMs = Math.max(
      lastAt,
      ...(appointmentAxisEndCandidates.length > 0
        ? appointmentAxisEndCandidates
        : [lastAt])
    );
    const axisStartMs = floorHourMs(firstAt) - HALF_HOUR_MS;
    const axisEndMs = ceilHalfHourMs(lastVisualMs);
    const axisSpanMs = Math.max(HOUR_MS, axisEndMs - axisStartMs);
    const totalHours = Math.max(1, axisSpanMs / HOUR_MS);
    const contentWidthPx =
      totalHours * PLANNER_TIMELINE_SCALE.hourWidthPx +
      TIMELINE_EDGE_PADDING_PX * 2;
    const renderWidthPx = Math.max(contentWidthPx, viewportWidthPx);
    const pxPerMs = getPlannerTimelinePxPerMs();
    const msToX = (atMs) =>
      TIMELINE_EDGE_PADDING_PX +
      ((atMs - axisStartMs) / axisSpanMs) *
        (contentWidthPx - TIMELINE_EDGE_PADDING_PX * 2);

    const allWithX = sorted.map((x) => ({
      ...x,
      xPx: msToX(x.atMs),
      style: resolveNodeCircleStyle(x.node),
    }));

    const milestones = allWithX.filter((x) => !isAppointmentNode(x.node));
    const appointmentsRaw = allWithX.filter((x) => isAppointmentNode(x.node));
    const windowBoundaries = milestones.filter((m) =>
      isWindowBoundaryNode(m.node)
    );
    const regularMilestones = milestones.filter(
      (m) => !isWindowBoundaryNode(m.node)
    );

    const appointmentBaseTopPx = AXIS_Y_PX + 10;

    const placedAppointments = placeInLanes(appointmentsRaw, (a) => {
      const range = getAppointmentWindowMs(a.node, a.atMs);
      const left = msToX(range.paddedStartMs);
      const right = msToX(range.paddedEndMs);
      return { left, right };
    }).map((a) => ({
      ...a,
      ...getAppointmentWindowMs(a.node, a.atMs),
      barTopPx: appointmentBaseTopPx + appointmentBarTopOffsetPx(a.laneIndex),
    }));

    const milestonesWithOverlapFlag = regularMilestones.map((m) => {
      const labelWidthPx = estimateLabelWidthPx(
        `${m.node?.title || ''} ${m.node?.time || ''}`,
        NODE_LABEL_LAYOUT_WIDTH_PX,
        NODE_LABEL_LAYOUT_WIDTH_PX
      );
      const labelLeft = m.xPx - labelWidthPx / 2;
      const labelRight = m.xPx + labelWidthPx / 2;
      const hasOverlapWithAppointment = placedAppointments.some((a) => {
        // Safety zone must include the light padded appointment range (dynamic from API).
        const apptLeft = msToX(a.paddedStartMs);
        const apptRight = msToX(a.paddedEndMs);
        const overlapWidthPx = intervalOverlapWidth(
          labelLeft,
          labelRight,
          apptLeft,
          apptRight
        );
        const horizontalOverlap = overlapWidthPx >= APPOINTMENT_LABEL_OVERLAP_MIN_PX;
        const closeToAxis =
          a.barTopPx <=
          AXIS_Y_PX +
            NODE_LABEL_BASE_OFFSET_PX +
            NODE_LABEL_ESTIMATED_HEIGHT_PX;
        return horizontalOverlap && closeToAxis;
      });

      return { ...m, labelWidthPx, hasOverlapWithAppointment };
    });

    const milestonesAboveAxis = milestonesWithOverlapFlag.filter(
      (m) => m.hasOverlapWithAppointment
    );
    const milestonesBelowAxis = milestonesWithOverlapFlag.filter(
      (m) => !m.hasOverlapWithAppointment
    );

    const placedAbove = placeSequentialNonOverlappingLanes(
      [...milestonesAboveAxis].sort((a, b) => a.xPx - b.xPx),
      (m) => ({
        left: m.xPx - m.labelWidthPx / 2,
        right: m.xPx + m.labelWidthPx / 2,
      })
    ).map((m) => ({
      ...m,
      labelTopPx:
        AXIS_Y_PX +
        NODE_LABEL_ABOVE_TOWARD_AXIS_PX -
        NODE_LABEL_ABOVE_GAP_PX -
        NODE_LABEL_ESTIMATED_HEIGHT_PX -
        m.laneIndex * MILESTONE_LANE_GAP_PX +
        (m.laneIndex >= 1 ? ABOVE_AXIS_SECOND_ROW_PULL_DOWN_PX : 0),
    }));

    const placedBelow = placeSequentialNonOverlappingLanes(
      [...milestonesBelowAxis].sort((a, b) => a.xPx - b.xPx),
      (m) => ({
        left: m.xPx - m.labelWidthPx / 2,
        right: m.xPx + m.labelWidthPx / 2,
      })
    ).map((m) => ({
      ...m,
      labelTopPx:
        AXIS_Y_PX +
        NODE_LABEL_BASE_OFFSET_PX +
        m.laneIndex * MILESTONE_LANE_GAP_PX,
    }));

    const combinedMilestones = [...placedAbove, ...placedBelow];
    const hasSecondRowAboveAxis = placedAbove.some((m) => m.laneIndex >= 1);
    const minMilestoneTopPx = combinedMilestones.reduce(
      (acc, m) => Math.min(acc, m.labelTopPx),
      AXIS_Y_PX
    );
    const minRequiredTopPx =
      WINDOW_LABEL_TOP_PX + 44 + AUTOBOOK_WINDOW_VERTICAL_NUDGE_PX;
    const shiftDownPx = Math.max(0, minRequiredTopPx - minMilestoneTopPx);
    const axisYPx = AXIS_Y_PX + shiftDownPx;
    const hasAnyTopNodeLabel = milestonesAboveAxis.length > 0;

    const windowLabelTopFullPx =
      WINDOW_LABEL_TOP_PX + shiftDownPx + AUTOBOOK_WINDOW_VERTICAL_NUDGE_PX;
    const windowLabelTopCompactPx = Math.max(
      windowLabelTopFullPx,
      axisYPx - WINDOW_LABEL_COMPACT_GAP_FROM_AXIS_PX
    );
    let windowLabelTopPx;
    if (!hasAnyTopNodeLabel) {
      windowLabelTopPx = windowLabelTopCompactPx;
    } else if (hasSecondRowAboveAxis) {
      windowLabelTopPx =
        windowLabelTopFullPx + WINDOW_LABEL_EXTRA_DOWN_WHEN_SECOND_ABOVE_ROW_PX;
    } else {
      windowLabelTopPx =
        windowLabelTopFullPx +
        (windowLabelTopCompactPx - windowLabelTopFullPx) *
          WINDOW_LABEL_SINGLE_ABOVE_ROW_BLEND;
    }
    const windowLineTopPx = windowLabelTopPx + 50;

    const nowLabelTopFullPx = WINDOW_LABEL_TOP_PX + 16 + shiftDownPx;
    const nowLabelTopCompactPx =
      nowLabelTopFullPx + (axisYPx - 75 - nowLabelTopFullPx) * 0.5;
    const nowLabelTopPx = hasAnyTopNodeLabel
      ? nowLabelTopFullPx
      : nowLabelTopCompactPx;
    const nowLineTopPx = nowLabelTopPx + 18;

    const finalMilestonesRaw = combinedMilestones.map((m) => ({
      ...m,
      labelTopPx: m.labelTopPx + shiftDownPx,
    }));

    const finalAppointmentsRaw = placedAppointments.map((a) => ({
      ...a,
      barTopPx: a.barTopPx + shiftDownPx,
    }));

    const milestoneBottomPxRaw = finalMilestonesRaw.reduce(
      (acc, m) => Math.max(acc, m.labelTopPx + NODE_LABEL_ESTIMATED_HEIGHT_PX),
      axisYPx
    );
    const appointmentBottomPxRaw = finalAppointmentsRaw.reduce(
      (acc, a) => Math.max(acc, a.barTopPx + APPOINTMENT_BAR_HEIGHT_PX + 18),
      axisYPx + 40
    );
    const topCandidates = [
      windowLabelTopPx,
      nowLabelTopPx,
      ...finalMilestonesRaw.map((m) => m.labelTopPx),
    ].filter((v) => Number.isFinite(v));
    const minOccupiedTopPx =
      topCandidates.length > 0 ? Math.min(...topCandidates) : 0;
    // Keep extra headroom so top-window row has breathing space.
    const topVisualPaddingPx = 20;
    const yLiftPx = Math.max(0, minOccupiedTopPx - topVisualPaddingPx);

    const axisYPxAdjusted = axisYPx - yLiftPx;
    const finalMilestones = finalMilestonesRaw.map((m) => ({
      ...m,
      labelTopPx: m.labelTopPx - yLiftPx,
    }));
    const finalAppointments = finalAppointmentsRaw.map((a) => ({
      ...a,
      barTopPx: a.barTopPx - yLiftPx,
    }));
    const windowLabelTopPxAdjusted = windowLabelTopPx - yLiftPx;
    const windowLineTopPxAdjusted = windowLineTopPx - yLiftPx;
    const nowLabelTopPxAdjusted = nowLabelTopPx - yLiftPx;
    const nowLineTopPxAdjusted = nowLineTopPx - yLiftPx;

    const milestoneBottomPx = milestoneBottomPxRaw - yLiftPx;
    const appointmentBottomPx = appointmentBottomPxRaw - yLiftPx;
    const tickTopPx = Math.max(milestoneBottomPx, appointmentBottomPx) + 8;
    const contentHeightPx = tickTopPx + 44;
    const navItems = [
      ...finalMilestones.map((m) => ({
        key: `node-${m.node.id ?? String(m.idx)}`,
        title: m.node?.title || 'Node',
        type: 'node',
        centerX: m.xPx,
      })),
      ...finalAppointments.map((a) => ({
        key: `appt-${a.node.id ?? String(a.idx)}`,
        title: a.node?.subtitle || a.node?.title || 'Appointment',
        type: 'appointment',
        centerX: (msToX(a.startMs) + msToX(a.endMs)) / 2,
      })),
    ].sort((a, b) => a.centerX - b.centerX);

    return {
      axisStartMs,
      axisEndMs,
      hourWidthPx: PLANNER_TIMELINE_SCALE.hourWidthPx,
      contentWidthPx: renderWidthPx,
      contentHeightPx,
      axisYPx: axisYPxAdjusted,
      ticks: buildHourTicks(axisStartMs, axisEndMs).map((tick) => ({
        ...tick,
        offsetPx: msToX(tick.ms),
      })),
      milestones: finalMilestones,
      windowBoundaries,
      appointments: finalAppointments,
      tickTopPx,
      msToX,
      pxPerMs,
      shiftDownPx,
      windowLabelTopPx: windowLabelTopPxAdjusted,
      windowLineTopPx: windowLineTopPxAdjusted,
      nowLabelTopPx: nowLabelTopPxAdjusted,
      nowLineTopPx: nowLineTopPxAdjusted,
      navItems,
    };
  }, [nodes, viewportWidthPx]);

  useEffect(() => {
    if (!timeline?.navItems?.length) {
      setActiveNavIndex(0);
      return;
    }
    setActiveNavIndex((prev) => {
      if (prev < 0) return 0;
      if (prev > timeline.navItems.length - 1) return timeline.navItems.length - 1;
      return prev;
    });
  }, [timeline]);

  useEffect(() => {
    if (!timeline?.navItems?.length) return;
    const container = viewportRef.current;
    if (!container) return;
    const active = timeline.navItems[activeNavIndex];
    if (!active) return;

    const targetLeft = Math.max(
      0,
      Math.min(
        active.centerX - container.clientWidth / 2,
        container.scrollWidth - container.clientWidth
      )
    );
    container.scrollTo({ left: targetLeft, behavior: 'smooth' });
  }, [activeNavIndex, timeline]);

  useEffect(() => {
    if (typeof onActiveNavItemChange !== 'function') return;
    const item = timeline?.navItems?.[activeNavIndex] || null;
    onActiveNavItemChange(item);
  }, [activeNavIndex, timeline, onActiveNavItemChange]);

  useImperativeHandle(
    ref,
    () => ({
      goPrev: () => {
        if (!timeline?.navItems?.length) return;
        setActiveNavIndex(
          (prev) => (prev - 1 + timeline.navItems.length) % timeline.navItems.length
        );
      },
      goNext: () => {
        if (!timeline?.navItems?.length) return;
        setActiveNavIndex((prev) => (prev + 1) % timeline.navItems.length);
      },
    }),
    [timeline]
  );

  if (timeline == null) {
    const fallback =
      String(windowStartLabel || '').trim() ||
      String(windowEndLabel || '').trim();
    return (
      <div className={styles.wrap}>
        <div className={styles.empty}>{fallback || 'No timeline data'}</div>
      </div>
    );
  }

  const nowInsideTimeline =
    nowMs >= timeline.axisStartMs && nowMs <= timeline.axisEndMs;
  const nowXPx = nowInsideTimeline ? timeline.msToX(nowMs) : null;
  const activeNavItem = timeline.navItems?.[activeNavIndex] || null;
  const activeKey = activeNavItem?.key || null;
  const nowTimeText = new Date(nowMs).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  return (
    <div className={styles.wrap}>
      <div ref={viewportRef} className={styles.viewport}>
        <div
          className={styles.content}
          style={{
            width: `${timeline.contentWidthPx}px`,
            minWidth: `${timeline.contentWidthPx}px`,
            height: `${timeline.contentHeightPx}px`,
          }}>
          <div
            className={styles.track}
            style={{
              top: `${timeline.axisYPx - TRACK_HEIGHT_PX / 2}px`,
              height: `${TRACK_HEIGHT_PX}px`,
            }}
          />

          {timeline.windowBoundaries.map((w) => (
            <React.Fragment key={`w-line-${w.node.id ?? w.idx}`}>
              <div
                className={styles.windowBoundaryLineLayer}
                style={{ left: `${w.xPx}px` }}>
                <div
                  className={styles.windowBoundaryLine}
                  style={{
                    top: `${timeline.windowLineTopPx}px`,
                    height: `${Math.max(
                      0,
                      timeline.tickTopPx - 6 - timeline.windowLineTopPx
                    )}px`,
                  }}
                />
              </div>
              <div
                className={styles.windowBoundaryLabelLayer}
                style={{ left: `${w.xPx}px` }}>
                <div
                  className={styles.windowBoundaryLabel}
                  style={{ top: `${timeline.windowLabelTopPx}px` }}>
                  {w.node.time ? (
                    <div className={styles.windowBoundaryTime}>
                      {w.node.time}
                    </div>
                  ) : null}
                  <div className={styles.windowBoundaryTitle}>
                    {isWindowStartNode(w.node)
                      ? 'Autobook\nWindow Start'
                      : 'Autobook\nWindow End'}
                  </div>
                </div>
              </div>
            </React.Fragment>
          ))}

          {nowInsideTimeline && nowXPx != null ? (
            <div
              className={styles.nowIndicator}
              style={{ left: `${nowXPx}px` }}>
              <div
                className={styles.nowLabel}
                style={{ top: `${timeline.nowLabelTopPx}px` }}>
                {nowTimeText}
              </div>
              <div
                className={styles.nowLine}
                style={{
                  top: `${timeline.nowLineTopPx}px`,
                  height: `${Math.max(
                    0,
                    timeline.tickTopPx - 6 - timeline.nowLineTopPx
                  )}px`,
                }}
              />
            </div>
          ) : null}

          {timeline.ticks.map((tick) => (
            <div
              key={tick.ms}
              className={styles.tick}
              style={{ left: `${tick.offsetPx}px` }}>
              <div
                className={styles.tickMark}
                style={{ top: `${timeline.axisYPx - 5}px` }}
              />
              <div
                className={styles.tickText}
                style={{ top: `${timeline.tickTopPx}px` }}>
                <div>{hourLabel(tick.ms)}</div>
                <div className={styles.tickDate}>{tickDateLabel(tick.ms)}</div>
              </div>
            </div>
          ))}

          {timeline.milestones.map((m) => {
            const radius = m.style.sizePx / 2;
            const isActive = activeKey === `node-${m.node.id ?? String(m.idx)}`;
            return (
              <div
                key={m.node.id ?? String(m.idx)}
                className={styles.node}
                style={{ left: `${m.xPx}px` }}>
                <div
                  className={styles.circle}
                  style={{
                    width: `${m.style.sizePx}px`,
                    height: `${m.style.sizePx}px`,
                    background: m.style.background,
                    border: m.style.border,
                    top: `${timeline.axisYPx - radius}px`,
                    boxShadow: isActive ? '0 0 0 3px #facc15' : 'none',
                  }}
                />
                <div
                  className={styles.nodeLabel}
                  style={{ top: `${m.labelTopPx}px` }}>
                  {m.node.title ? (
                    <p className={styles.title}>{m.node.title}</p>
                  ) : null}
                  {m.node.subtitle ? (
                    <p className={styles.subtitle}>{m.node.subtitle}</p>
                  ) : null}
                  {m.node.time ? (
                    <p className={styles.time}>{m.node.time}</p>
                  ) : null}
                  {m.node.extra ? (
                    <p className={styles.extra}>{m.node.extra}</p>
                  ) : null}
                </div>
              </div>
            );
          })}

          {timeline.appointments.map((a) => (
            <div key={a.node.id ?? String(a.idx)} className={styles.appointment}>
              {(() => {
                const isActive = activeKey === `appt-${a.node.id ?? String(a.idx)}`;
                return (
                  <div
                    className={styles.appointmentBar}
                    style={{
                      left: `${timeline.msToX(a.startMs)}px`,
                      top: `${a.barTopPx}px`,
                      width: `${Math.max(
                        0,
                        durationMsToPx(a.endMs - a.startMs)
                      )}px`,
                      background: a.style.background,
                      boxShadow: isActive ? '0 0 0 3px #facc15' : 'none',
                    }}>
                    {a.node.subtitle || a.node.title}
                  </div>
                );
              })()}
              <div
                className={styles.appointmentCircle}
                style={{
                  left: `${a.xPx}px`,
                  top: `${timeline.axisYPx - 5}px`,
                  background: a.style.background,
                }}
              />
              <div
                className={styles.appointmentBufferBar}
                style={{
                  left: `${timeline.msToX(a.paddedStartMs)}px`,
                  top: `${a.barTopPx}px`,
                  width: `${Math.max(
                    0,
                    durationMsToPx(a.paddedEndMs - a.paddedStartMs)
                  )}px`,
                  background: a.style.background,
                }}
              />
              {a.node.time ? (
                <div
                  className={styles.appointmentTime}
                  style={{
                    left: `${
                      (timeline.msToX(a.startMs) + timeline.msToX(a.endMs)) / 2
                    }px`,
                    top: `${a.barTopPx + APPOINTMENT_BAR_HEIGHT_PX + 4}px`,
                  }}>
                  {a.node.time}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>
      <div className={styles.axisMeta}>
        <span>{windowStartLabel || dateLabel(timeline.axisStartMs)}</span>
        <span>{windowEndLabel || dateLabel(timeline.axisEndMs)}</span>
      </div>
    </div>
  );
});
